import React, { useEffect, useMemo, useState } from 'react';
import { Btn, Loading, Msg, Section, fmtDate, panelPost } from '../kit';
import { Pill, num, rowsOf, str } from '../ipintel/shared';
import { type HzCtx, S, TextBlock, fmtCost, fmtN, shortId } from './shared';

/**
 * Prompts — GET horizon/prompts: every saved Horizon prompt version with its
 * full texts (master prompt, horizon definitions, problem / value lever
 * definitions), how much it produced, and whether it is the FINAL one (first
 * in the source priority). Below them, the prompt files as they are now in
 * Backend/horizon/prompts/ and whether they still match the final prompt.
 *
 * The Source priority editor decides which prompt's results the public tech
 * pages show (POST horizon/source_save {prompt_priority: [ids]}; the server
 * then rebuilds horizon_current).
 */

type Res = { data: any; loading: boolean; error: string; reload: () => void };

const TEXTS: { key: string; label: string }[] = [
  { key: 'master_prompt', label: 'Master prompt' },
  { key: 'horizon_md', label: 'Horizon definitions / sector examples (horizon_md)' },
  { key: 'problem_md', label: 'Problem-type / value lever definitions (problem_md)' },
];

/** files arrives as {name: text} or {name: {content, matches}} or [{name, content, matches}]. */
const filesOf = (v: any): { name: string; content: string; matches: boolean | null; note: string }[] => {
  const one = (name: string, x: any) => {
    if (typeof x === 'string') return { name, content: x, matches: null, note: '' };
    const m = x?.matches ?? x?.match ?? x?.matches_final ?? x?.same;
    return {
      name: str(x?.name) || name,
      content: str(x?.content ?? x?.text),
      matches: typeof m === 'boolean' ? m : null,
      note: str(x?.note || (x?.column ? `compared with the final prompt's ${x.column}` : '') || x?.error || ''),
    };
  };
  if (Array.isArray(v)) return v.map((x: any, i: number) => one(str(x?.name) || `file ${i + 1}`, x));
  if (v && typeof v === 'object') return Object.entries(v).map(([k, x]) => one(k, x));
  return [];
};

const HzPrompts: React.FC<{
  ctx: HzCtx; res: Res; source: any; focus: { id: string; n: number } | null;
}> = ({ ctx, res, source, focus }) => {
  const rows = useMemo(() => rowsOf(res.data, 'prompts'), [res.data]);
  const files = useMemo(() => filesOf(res.data?.files), [res.data]);
  const runner = res.data?.runner || null;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [openFile, setOpenFile] = useState<Record<string, boolean>>({});

  // A prompt named from elsewhere (a run, a result, the source list): open its card and bring it into view.
  useEffect(() => {
    if (!focus?.id) return;
    setOpen(o => ({ ...o, [focus.id]: true }));
    const t = setTimeout(() => {
      document.getElementById(`hz-prompt-${focus.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [focus?.n, focus?.id]);

  // ---- Source priority editor ------------------------------------------
  const serverOrder = useMemo<string[]>(() => {
    const fromSource = rowsOf(source?.prompt_priority).map((p: any) => str(p?.id ?? p)).filter(Boolean);
    if (fromSource.length) return fromSource;
    return rows.filter((r: any) => r?.priority_index !== null && r?.priority_index !== undefined && r?.priority_index !== '')
      .sort((a: any, b: any) => num(a.priority_index) - num(b.priority_index)).map((r: any) => str(r.id));
  }, [source, rows]);
  const [order, setOrder] = useState<string[]>(serverOrder);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  useEffect(() => { if (!dirty) setOrder(serverOrder); }, [serverOrder, dirty]);

  const byId = (id: string) => rows.find((r: any) => str(r?.id) === id);
  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= order.length) return;
    const next = [...order];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next); setDirty(true); setMsg(null);
  };
  const exclude = (id: string) => { setOrder(o => o.filter(x => x !== id)); setDirty(true); setMsg(null); };
  const include = (id: string) => { setOrder(o => [...o, id]); setDirty(true); setMsg(null); };
  const excluded = rows.map((r: any) => str(r?.id)).filter((id: string) => id && !order.includes(id));

  const save = async (reset = false) => {
    setSaving(true); setMsg(null);
    const { data, error } = await panelPost(S, 'source_save', reset ? { reset: true } : { prompt_priority: order });
    setSaving(false);
    if (error) { setMsg({ kind: 'error', text: `Not saved: ${error}` }); return; }
    const rf = data?.refresh || data?.refreshed || data?.counts;
    const rows_ = rf && typeof rf === 'object' ? (rf.rows ?? rf.current_rows ?? rf.inserted) : (data?.current_rows ?? undefined);
    setMsg({
      kind: 'ok',
      text: `Saved — the tech pages now use this order.${rows_ !== undefined ? ` Current data rebuilt: ${num(rows_).toLocaleString()} technologies.` : ' Current data is being rebuilt.'}`,
    });
    setDirty(false);
    ctx.reloadAll();
  };

  if (res.loading && !res.data) return <Loading label="Reading prompts…" />;
  return (
    <div className="flex flex-col gap-4 min-w-0">
      {res.error && <Msg kind="error">{res.error}</Msg>}

      <Section title="Source priority — which prompt's results the tech pages show"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            {dirty && <Btn small onClick={() => { setOrder(serverOrder); setDirty(false); setMsg(null); }}>Undo changes</Btn>}
            {!source?.is_default && !dirty && (
              <Btn small onClick={() => save(true)} disabled={saving}
                title="Forget the saved order and use the default (the three full-catalogue prompts, final first; newer prompts only when added here)">Reset to default</Btn>
            )}
            <Btn small kind="primary" onClick={() => save()} disabled={!dirty || saving || !order.length}
              title={order.length ? 'Save the order and rebuild the current data' : 'Keep at least one prompt'}>
              {saving ? 'Saving…' : 'Save priority'}
            </Btn>
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1">
          A technology's current data is its newest usable result under the first prompt in this list that has one; a
          prompt lower down only fills technologies the ones above never covered. The first prompt is the FINAL one.
          Saving rebuilds the current data for every technology.
        </p>
        {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
        {order.length ? (
          <ol className="flex flex-col gap-1.5">
            {order.map((id, i) => {
              const r = byId(id);
              return (
                <li key={id} className="flex items-center gap-2 flex-wrap rounded-lg border border-grayscale-300 bg-white px-3 py-2 min-w-0">
                  <span className="w-5 font-bold text-grayscale-1100 tabular-nums">{i + 1}.</span>
                  <span className="min-w-0 flex-1 flex flex-col">
                    <span className="flex items-center gap-2 flex-wrap text-sm">
                      <span className="font-medium text-grayscale-1300 break-words">{str(r?.name) || shortId(id)}</span>
                      {i === 0 ? <Pill tone="good">FINAL</Pill> : <Pill tone="neutral">fallback {i}</Pill>}
                      {r && num(r.results) === 0 && <Pill tone="warn" title="This prompt has no results — it cannot fill anything">no results</Pill>}
                    </span>
                    <span className="text-[11px] text-grayscale-1100">
                      <span className="font-mono">{shortId(id)}</span>{r?.created_at ? ` · ${fmtDate(r.created_at)}` : ''}
                      {r ? ` · ${fmtN(r.results)} results · ${fmtN(r.techs)} technologies` : ' · not in the prompt list'}
                      {r && r.current_rows !== undefined ? ` · ${fmtN(r.current_rows)} shown on pages now` : ''}
                    </span>
                  </span>
                  <span className="inline-flex gap-1">
                    <Btn small onClick={() => move(i, -1)} disabled={i === 0} title="Move up">↑</Btn>
                    <Btn small onClick={() => move(i, 1)} disabled={i === order.length - 1} title="Move down">↓</Btn>
                    <Btn small onClick={() => exclude(id)} title="Stop using this prompt's results">Exclude</Btn>
                  </span>
                </li>
              );
            })}
          </ol>
        ) : <Msg kind="error">No prompt included — the tech pages would have no Horizon data. Include at least one.</Msg>}
        {excluded.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <div className="text-xs text-grayscale-1100">Not used by the tech pages:</div>
            {excluded.map((id: string) => {
              const r = byId(id);
              return (
                <div key={id} className="flex items-center gap-2 flex-wrap text-sm px-3 py-1.5 rounded-lg border border-dashed border-grayscale-300 min-w-0">
                  <span className="min-w-0 flex-1 break-words text-grayscale-1200">
                    {str(r?.name) || shortId(id)}
                    <span className="text-[11px] text-grayscale-1100"> · <span className="font-mono">{shortId(id)}</span>
                      {r?.created_at ? ` · ${fmtDate(r.created_at)}` : ''} · {fmtN(r?.results)} results</span>
                  </span>
                  <Btn small onClick={() => include(id)}>Include</Btn>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      <Section title={`Prompts (${rows.length})`} right={<Btn small onClick={res.reload} disabled={res.loading}>Refresh</Btn>}>
        <p className="text-xs text-grayscale-1100 -mt-1">
          Every prompt version saved by the sandbox and the batch runs, newest first. A prompt is identified by the hash
          of its three texts, so several can share a name.
        </p>
        <div className="flex flex-col gap-3">
          {[...rows].sort((a: any, b: any) => str(b.created_at).localeCompare(str(a.created_at))).map((r: any) => {
            const id = str(r.id);
            // The saved priority, not the editor's unsaved order.
            const pos = serverOrder.indexOf(id);
            const isOpen = !!open[id];
            return (
              <div key={id} id={`hz-prompt-${id}`}
                className={`rounded-xl border bg-white p-4 flex flex-col gap-3 min-w-0 ${pos === 0 ? 'border-green-300 ring-1 ring-green-200' : 'border-grayscale-300'}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-grayscale-1300 font-grotesk break-words">{str(r.name) || shortId(id)}</span>
                      {pos === 0 && <Pill tone="good" title="First in the source priority — the prompt finally used">FINAL</Pill>}
                      {pos > 0 && <Pill tone="info" title="Fills technologies the prompts above have no result for">priority {pos + 1}</Pill>}
                      {pos < 0 && <Pill tone="neutral">not used by the tech pages</Pill>}
                      {r.matches_files === true && (
                        <Pill tone="info" title="Backend/horizon/prompts/ hashes to this prompt — the batch runner would use it">
                          = files in horizon/prompts
                        </Pill>
                      )}
                    </div>
                    <div className="text-[11px] text-grayscale-1100 break-all">
                      <span className="font-mono">{id}</span>
                      {r.content_hash ? <> · hash <span className="font-mono">{shortId(r.content_hash, 12)}</span></> : null}
                      {r.created_at ? ` · created ${fmtDate(r.created_at)}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Btn small onClick={() => ctx.goResults({ prompt: id })} disabled={!num(r.results)}>Results →</Btn>
                    <Btn small onClick={() => ctx.goTech({ prompt: id })} disabled={!num(r.techs)}>Technologies →</Btn>
                    <Btn small onClick={() => setOpen(o => ({ ...o, [id]: !isOpen }))}>{isOpen ? 'Hide texts' : 'Show full texts'}</Btn>
                  </div>
                </div>
                <div className="flex gap-4 flex-wrap text-xs text-grayscale-1200">
                  <span><span className="font-bold text-grayscale-1300 tabular-nums">{fmtN(r.results)}</span> results</span>
                  <span><span className="font-bold text-grayscale-1300 tabular-nums">{fmtN(r.techs)}</span> technologies</span>
                  <span><span className="font-bold text-grayscale-1300 tabular-nums">{fmtN(r.runs)}</span> runs</span>
                  {r.ok !== undefined && <span><span className="font-bold text-grayscale-1300 tabular-nums">{fmtN(r.ok)}</span> usable</span>}
                  {r.errors !== undefined && <span><span className="font-bold text-grayscale-1300 tabular-nums">{fmtN(r.errors)}</span> errors</span>}
                  {r.current_rows !== undefined && <span><span className="font-bold text-grayscale-1300 tabular-nums">{fmtN(r.current_rows)}</span> shown on pages</span>}
                  {r.cost !== undefined && <span>{fmtCost(r.cost)}{r.tokens !== undefined ? ` · ${fmtN(r.tokens)} tokens` : ''}</span>}
                  {TEXTS.map(t => (
                    <span key={t.key} className="text-grayscale-1100">{t.key}: {str(r[t.key]).length.toLocaleString()} chars</span>
                  ))}
                </div>
                {isOpen && TEXTS.map(t => (
                  <div key={t.key} className="flex flex-col gap-1 min-w-0">
                    <div className="text-xs font-bold text-grayscale-1200">{t.label}</div>
                    <TextBlock text={r[t.key]} mono maxHeight="28rem" empty="Empty in this prompt." />
                  </div>
                ))}
              </div>
            );
          })}
          {!rows.length && <div className="text-sm text-grayscale-1100">No prompts saved yet.</div>}
        </div>
      </Section>

      <Section title="Prompt files in Backend/horizon/prompts/">
        <p className="text-xs text-grayscale-1100 -mt-1">
          What the batch runner (horizon/scripts/horizon_batch_run_v2.py) reads today. If a file differs from the final
          prompt's stored text, the next run on pending technologies would use a different prompt from the one the
          current data came from.
        </p>
        {runner && (
          <div className={`text-xs rounded-lg px-3 py-2 border ${runner.matches_final
            ? 'text-green-700 bg-green-50 border-green-200' : 'text-amber-700 bg-amber-50 border-amber-200'}`}>
            {runner.prompt_id
              ? <>The three files together hash to <span className="font-medium">{str(runner.prompt_name) || shortId(runner.prompt_id)}</span>{' '}
                  (<span className="font-mono">{shortId(runner.prompt_id)}</span>){runner.matches_final ? ' — the FINAL prompt ✓. A run on pending technologies adds results to it.'
                  : ' — not the FINAL prompt ⚠. A run would add results under that prompt, which the tech pages use only as a fallback (or not at all).'}</>
              : <>The files do not match any saved prompt ⚠ — the next run would save a new prompt version, which the tech pages
                  will not use until it is put first in the priority.</>}
            {runner.script && <span className="text-grayscale-1100"> · runner: <span className="font-mono">{str(runner.script)}</span></span>}
          </div>
        )}
        {files.length ? files.map(fl => (
          <div key={fl.name} className="flex flex-col gap-1.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm text-grayscale-1300 break-all">{fl.name}</span>
              {fl.matches === true && <Pill tone="good">matches the final prompt ✓</Pill>}
              {fl.matches === false && <Pill tone="warn">differs from the final prompt ⚠</Pill>}
              {fl.matches === null && <Pill tone="neutral">not compared</Pill>}
              <span className="text-[11px] text-grayscale-1100">{fl.content.length.toLocaleString()} chars{fl.note ? ` · ${fl.note}` : ''}</span>
              <Btn small onClick={() => setOpenFile(o => ({ ...o, [fl.name]: !o[fl.name] }))}>
                {openFile[fl.name] ? 'Hide' : 'Show'}
              </Btn>
            </div>
            {openFile[fl.name] && <TextBlock text={fl.content} mono maxHeight="28rem" empty="The file is empty or missing." />}
          </div>
        )) : <div className="text-sm text-grayscale-1100">The server did not list the prompt files.</div>}
      </Section>
    </div>
  );
};

export default HzPrompts;
